import macro from '@kitware/vtk.js/macros.js';
import vtkOpenGLTexture from '@kitware/vtk.js/Rendering/OpenGL/Texture.js';
import cache from '../../cache/cache.js';
import { getConstructorFromType } from '../../utilities/getBufferConfiguration.js';
import VoxelManager from '../../utilities/VoxelManager.js';
import { voxelGridsEqual } from '../../utilities/voxelGrid/index.js';
import { requireVoxelStatistic } from '../../utilities/voxelGrid/voxelStatistics.js';
import VoxelStatistics from '../../enums/VoxelStatistics.js';
import ImageQualityStatus from '../../enums/ImageQualityStatus.js';
import autoLoad from '../../utilities/autoLoad.js';
import { getConfiguration } from '../../init.js';
const DEFAULT_MAX_SLICES_PER_RENDER = 16;
const DEFAULT_MAX_MILLISECONDS_PER_RENDER = 8;
function reducedTextureFillBudget() {
    const budget = getConfiguration()?.rendering?.reducedTextureFill;
    return {
        maxSlicesPerRender: budget?.maxSlicesPerRender ?? DEFAULT_MAX_SLICES_PER_RENDER,
        maxMillisecondsPerRender: budget?.maxMillisecondsPerRender ?? DEFAULT_MAX_MILLISECONDS_PER_RENDER,
    };
}
function convertDataType(data, targetDataType) {
    const Constructor = getConstructorFromType(targetDataType);
    const convertedData = new Constructor(data.length);
    convertedData.set(data);
    return convertedData;
}
function vtkStreamingOpenGLTexture(publicAPI, model) {
    model.classHierarchy.push('vtkStreamingOpenGLTexture');
    model.updatedFrames = [];
    model.volumeId = null;
    model.fillCursor = 0;
    model.remainingFillScheduled = false;
    model.grid = null;
    model.statistic = null;
    const superCreate3DFilterableFromRaw = publicAPI.create3DFilterableFromRaw;
    publicAPI.create3DFilterableFromRaw = ({ width, height, depth, numberOfComponents, dataType, data, preferSizeOverAccuracy, }) => {
        model.inputDataType = dataType;
        model.inputNumComps = numberOfComponents;
        superCreate3DFilterableFromRaw({
            width,
            height,
            depth,
            numberOfComponents,
            dataType,
            data,
            preferSizeOverAccuracy,
        });
    };
    const superCreate3DFromRaw = publicAPI.create3DFromRaw;
    publicAPI.create3DFromRaw = (options) => {
        const created = superCreate3DFromRaw(options);
        if (created && !options.data) {
            publicAPI.markAllFramesUpdated();
        }
        return created;
    };
    const superUpdate = publicAPI.updateVolumeInfoForGL;
    publicAPI.updateVolumeInfoForGL = (dataType, numComps) => {
        const isScalingApplied = superUpdate(dataType, numComps);
        model.volumeInfo.dataComputedScale = [1];
        model.volumeInfo.dataComputedOffset = [0];
        return isScalingApplied;
    };
    publicAPI.update3DFromRaw = () => {
        const { volumeId } = model;
        if (!volumeId) {
            return;
        }
        const volume = cache.getVolume(volumeId);
        if (!volume) {
            return;
        }
        model._openGLRenderWindow.activateTexture(publicAPI);
        publicAPI.createTexture();
        publicAPI.bind();
        if (volume.isDynamicVolume()) {
            updateDynamicVolumeTexture();
            return;
        }
        if (model.grid && !isGridOfVolume(volume, model.grid)) {
            return publicAPI.hasUpdatedFrames() && updateTextureFromComposite(volume);
        }
        return (publicAPI.hasUpdatedFrames() && updateTextureImagesUsingVoxelManager());
    };
    function isGridOfVolume(volume, grid) {
        return volume.voxelGrid ? voxelGridsEqual(volume.voxelGrid, grid) : true;
    }
    function boxFactorsOf(volume, grid) {
        const volumeGrid = volume.voxelGrid;
        if (!volumeGrid) {
            return null;
        }
        const factors = [];
        for (let axis = 0; axis < 3; axis++) {
            for (let index = 0; index < 3; index++) {
                if (Math.abs(grid.direction[axis * 3 + index] -
                    volumeGrid.direction[axis * 3 + index]) > 1e-6) {
                    return null;
                }
            }
            const ratio = grid.spacing[axis] / volumeGrid.spacing[axis];
            const factor = Math.round(ratio);
            if (factor < 1 || Math.abs(ratio - factor) > 1e-6) {
                return null;
            }
            factors.push(factor);
        }
        return factors;
    }
    function framesOfSlice(volume, factorK, slice) {
        const [sourceWidth, sourceHeight, sourceDepth] = volume.dimensions;
        const voxelManager = volume.voxelManager;
        const firstK = slice * factorK;
        if (!voxelManager?.getSliceData || firstK >= sourceDepth) {
            return null;
        }
        const lastK = Math.min(firstK + factorK, sourceDepth);
        const centre = (firstK + lastK - 1) / 2;
        const loaded = [];
        const replicates = [];
        for (let k = firstK; k < lastK; k++) {
            const quality = volume.getImageQuality
                ? volume.getImageQuality(k)
                : ImageQualityStatus.FULL_RESOLUTION;
            if (quality >= ImageQualityStatus.SUBRESOLUTION) {
                loaded.push(k);
            }
            else if (quality) {
                replicates.push(k);
            }
        }
        const indices = loaded.length ? loaded : nearestTo(centre, replicates);
        const frameLength = sourceWidth * sourceHeight;
        const frames = [];
        for (const k of indices) {
            const frame = readFrame(volume, k, frameLength);
            if (frame) {
                frames.push(frame);
            }
        }
        return frames;
    }
    function nearestTo(centre, indices) {
        let best = Infinity;
        for (const index of indices) {
            best = Math.min(best, Math.abs(index - centre));
        }
        return indices.filter((index) => Math.abs(index - centre) === best);
    }
    function readFrame(volume, k, frameLength) {
        const imageId = volume.imageIds?.[k];
        const image = imageId ? cache.getImage(imageId) : undefined;
        if (imageId && !image) {
            return null;
        }
        let frame = image?.voxelManager?.getScalarData();
        if (!frame || frame.length < frameLength) {
            try {
                frame = volume.voxelManager.getSliceData({
                    sliceIndex: k,
                    slicePlane: 2,
                });
            }
            catch {
                frame = null;
            }
        }
        return frame && frame.length >= frameLength ? frame : null;
    }
    function fillSliceByBoxStatistic(volume, grid, factors, slice, target) {
        const statistic = volume.reductionStatistic ?? VoxelStatistics.Average;
        const { fillPlane } = requireVoxelStatistic(statistic);
        if (!fillPlane) {
            return false;
        }
        const [factorI, factorJ, factorK] = factors;
        const frames = framesOfSlice(volume, factorK, slice);
        if (!frames?.length) {
            return false;
        }
        fillPlane(frames, [volume.dimensions[0], volume.dimensions[1]], [grid.dimensions[0], grid.dimensions[1]], [factorI, factorJ], target.getScalarData());
        return true;
    }
    function derivedSliceReaderOf(composite, grid, statistic) {
        const representation = composite.getRepresentation(grid, statistic ?? VoxelStatistics.Average);
        const [width, height, depth] = grid.dimensions;
        const frameLength = width * height;
        if (!representation) {
            return null;
        }
        const { imageIds } = representation;
        if (imageIds?.length === depth) {
            return (slice) => cache.getImage(imageIds[slice])?.voxelManager?.getScalarData();
        }
        const voxels = representation.voxelManager?.getWritableScalarData?.();
        if (voxels?.length !== frameLength * depth) {
            return null;
        }
        return (slice) => voxels.subarray(slice * frameLength, (slice + 1) * frameLength);
    }
    function scheduleRemainingFill() {
        if (model.remainingFillScheduled) {
            return;
        }
        model.remainingFillScheduled = true;
        setTimeout(() => {
            model.remainingFillScheduled = false;
            if (!model.volumeId || !publicAPI.hasUpdatedFrames()) {
                return;
            }
            publicAPI.modified();
            autoLoad(model.volumeId);
        }, 0);
    }
    function updateTextureFromComposite(volume) {
        const { grid } = model;
        const [width, height, depth] = grid.dimensions;
        const Constructor = getConstructorFromType(volume.dataType, false);
        const frameLength = width * height;
        const composite = volume.compositeVoxelManager;
        const gl = model.context;
        const statistic = volume.reductionStatistic ?? VoxelStatistics.Average;
        const derivedSliceOf = derivedSliceReaderOf(composite, grid, statistic);
        const factors = boxFactorsOf(volume, grid);
        const slab = VoxelManager.createScalarVolumeVoxelManager({
            dimensions: [width, height, 1],
            scalarData: new Constructor(frameLength),
            numberOfComponents: 1,
        });
        const { maxSlicesPerRender, maxMillisecondsPerRender } = reducedTextureFillBudget();
        const startTime = performance.now();
        let slicesHandled = 0;
        let slicesWithData = 0;
        let stoppedEarly = false;
        for (let step = 0; step < depth; step++) {
            const slice = (model.fillCursor + step) % depth;
            if (!model.updatedFrames[slice]) {
                continue;
            }
            if (slicesWithData >= maxSlicesPerRender ||
                (slicesHandled &&
                    performance.now() - startTime >= maxMillisecondsPerRender)) {
                model.fillCursor = slice;
                stoppedEarly = true;
                break;
            }
            let data = derivedSliceOf?.(slice);
            let hasData = !!data;
            if (!data) {
                slab.getScalarData().fill(0);
                const filled = factors &&
                    fillSliceByBoxStatistic(volume, grid, factors, slice, slab);
                hasData = !!filled || !factors;
                if (!filled && !factors) {
                    composite.fillGrid({
                        dimensions: [width, height, 1],
                        spacing: grid.spacing,
                        direction: grid.direction,
                        origin: [
                            grid.origin[0] + grid.direction[6] * grid.spacing[2] * slice,
                            grid.origin[1] + grid.direction[7] * grid.spacing[2] * slice,
                            grid.origin[2] + grid.direction[8] * grid.spacing[2] * slice,
                        ],
                    }, slab, statistic);
                }
                data = slab.getScalarData();
            }
            if (volume.dataType !== data.constructor.name) {
                data = convertDataType(data, volume.dataType);
            }
            const [pixData] = publicAPI.updateArrayDataTypeForGL(volume.dataType, [
                data,
            ]);
            publicAPI.bind();
            gl.texSubImage3D(model.target, 0, 0, 0, slice, width, height, 1, model.format, model.openGLDataType, pixData);
            publicAPI.deactivate();
            model.updatedFrames[slice] = null;
            slicesHandled++;
            if (hasData) {
                slicesWithData++;
            }
        }
        if (stoppedEarly) {
            scheduleRemainingFill();
        }
        if (model.generateMipmap) {
            model.context.generateMipmap(model.target);
        }
        publicAPI.deactivate();
        return true;
    }
    publicAPI.setUpdatedFrame = (frameIndex) => {
        model.updatedFrames[frameIndex] = true;
        publicAPI.modified();
    };
    publicAPI.markAllFramesUpdated = () => {
        const volume = cache.getVolume(model.volumeId);
        if (!volume) {
            return;
        }
        const slices = model.grid
            ? model.grid.dimensions[2]
            : volume.imageIds.length;
        for (let i = 0; i < slices; i++) {
            model.updatedFrames[i] = true;
        }
        publicAPI.modified();
    };
    function updateTextureImagesUsingVoxelManager() {
        const volume = cache.getVolume(model.volumeId);
        const imageIds = volume.imageIds;
        for (let i = 0; i < model.updatedFrames.length; i++) {
            if (model.updatedFrames[i]) {
                const image = cache.getImage(imageIds[i]);
                if (!image) {
                    continue;
                }
                let data = image.voxelManager.getScalarData();
                const gl = model.context;
                if (data.length !==
                    model.width * model.height * (model.components || 1)) {
                    try {
                        data = volume.voxelManager.getSliceData({
                            sliceIndex: i,
                            slicePlane: 2,
                        });
                    }
                    catch {
                        data = null;
                    }
                    if (!data) {
                        continue;
                    }
                }
                if (volume.dataType !== data.constructor.name) {
                    data = convertDataType(data, volume.dataType);
                }
                const [pixData] = publicAPI.updateArrayDataTypeForGL(volume.dataType, [
                    data,
                ]);
                publicAPI.bind();
                const zOffset = i;
                gl.texSubImage3D(model.target, 0, 0, 0, zOffset, model.width, model.height, 1, model.format, model.openGLDataType, pixData);
                publicAPI.deactivate();
                model.updatedFrames[i] = null;
            }
        }
        if (model.generateMipmap) {
            model.context.generateMipmap(model.target);
        }
        publicAPI.deactivate();
        return true;
    }
    function updateDynamicVolumeTexture() {
        const volume = cache.getVolume(model.volumeId);
        const imageIds = volume.getCurrentDimensionGroupImageIds();
        if (!imageIds.length) {
            return false;
        }
        let constructor;
        for (let i = 0; i < imageIds.length; i++) {
            const imageId = imageIds[i];
            const image = cache.getImage(imageId);
            let data;
            if (!image) {
                constructor = getConstructorFromType(volume.dataType, true);
                data = new constructor(model.width * model.height);
            }
            else {
                data = image.voxelManager.getScalarData();
                constructor = data.constructor;
            }
            const gl = model.context;
            if (volume.dataType !== data.constructor.name) {
                data = convertDataType(data, volume.dataType);
            }
            const [pixData] = publicAPI.updateArrayDataTypeForGL(volume.dataType, [
                data,
            ]);
            publicAPI.bind();
            let zOffset = i;
            gl.texSubImage3D(model.target, 0, 0, 0, zOffset, model.width, model.height, 1, model.format, model.openGLDataType, pixData);
            publicAPI.deactivate();
        }
        if (model.generateMipmap) {
            model.context.generateMipmap(model.target);
        }
        publicAPI.deactivate();
        return true;
    }
    publicAPI.hasUpdatedFrames = () => !model.updatedFrames.length || model.updatedFrames.some((frame) => frame);
    publicAPI.getUpdatedFrames = () => model.updatedFrames;
    publicAPI.setVolumeId = (volumeId) => {
        model.volumeId = volumeId;
    };
    publicAPI.getVolumeId = () => model.volumeId;
    publicAPI.setGrid = (grid) => {
        model.grid = grid;
    };
    publicAPI.getGrid = () => model.grid;
    publicAPI.setStatistic = (statistic) => {
        model.statistic = statistic;
    };
    publicAPI.getStatistic = () => model.statistic;
    publicAPI.setTextureParameters = ({ width, height, depth, numberOfComponents, dataType, }) => {
        model.width ??= width;
        model.height ??= height;
        model.depth ??= depth;
        model.inputNumComps ??= numberOfComponents;
        model.inputDataType ??= dataType;
    };
    publicAPI.getTextureParameters = () => ({
        width: model.width,
        height: model.height,
        depth: model.depth,
        numberOfComponents: model.inputNumComps,
        dataType: model.inputDataType,
    });
}
const DEFAULT_VALUES = {
    updatedFrames: [],
};
export function extend(publicAPI, model, initialValues = {}) {
    Object.assign(model, DEFAULT_VALUES, initialValues);
    vtkOpenGLTexture.extend(publicAPI, model, initialValues);
    vtkStreamingOpenGLTexture(publicAPI, model);
}
export const newInstance = macro.newInstance(extend, 'vtkStreamingOpenGLTexture');
export default { newInstance, extend };
